import { useCallback, useState } from "react";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useNavigate } from "react-router";
import { processError } from "../../../../common/utils/base_error";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { initialBancoPagamentoFiltersValues } from "../hooks/banco_pagamento_lista/filters";
import { RateioGeralContaPagarLista } from "../../domain/models/rateio_geral_contas_pagar_lista";
import { useRateioGeralListaFilters } from "../hooks/rateio_geral_lista/filters";
import { useRateioGeralListaPagination } from "../hooks/rateio_geral_lista/pagination";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { ReportRateioGeralForm } from "../schemas/reports/rateioGeralReport";

type LoadRateioGeralListaState = {
    idsCentroCusto?: number[];
    nroNotaFiscal?: string | null;
    dataDe?: Date | null;
    dataAte?: Date | null;

    page?: number;
    rowsPerPage?: number;

}

export const useRateioGeralListaViewModel = () => {

    const {
        reports: {
            getReportRateioGeralListaUseCase,
        },
        financeiro: {
            getAllRateioGeralContaPagarUseCase,
            deleteRateioGeralUseCase,

        },
        commons: {
            getCentroCustoDropDownUseCase,
            getBancosPagamentoDropDownUseCase,
            getOrdemComprasDropDownUseCase,
            getSubcontasDropDownUseCase,
            getFornecedorDropDownUseCase,
            getFuncionariosDropDownUseCase,
            getProdutoServicoDropDownUseCase,
            getMedicaoTeceirizacaoDropDownUseCase,
        },

    } = useDependencyInjection();


    const navigate = useNavigate();
    const { pushErrorNotification, pushInfoNotification, setPageLoading } = usePageLoader();
    const debouncedRef = useCreateDebounceHandler(1500);
    const [reportViewerState, startReportViewerProcess] = useReportViewer();

    const [openRelatorioModal, setOpenRelatorioModal] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [selectedIdRateioGeral, setSelectedIdRateioGeral] = useState<number>();

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [errorMessage, setErrorMessage] = useState<string>();
    const [rateioGeralContaPagarLista, setRateioGeralContaPagarLista] = useState<RateioGeralContaPagarLista[]>();


    const filters = useRateioGeralListaFilters();
    const pagination = useRateioGeralListaPagination();


    //#region DROPDOWN ESTADOS.

    const [ddlCentroCusto, setDdlCentroCusto] = useState<DropDown[]>();
    const [ddlOrdemCompra, setDdlOrdemCompra] = useState<DropDown[]>();
    const [ddlFornecedor, setDdlFornecedor] = useState<DropDown[]>();
    const [ddlFuncionario, setDdlFuncionario] = useState<DropDown[]>();
    const [ddlPgtoMedicao, setDdlPgtoMedicao] = useState<DropDown[]>();

    //#endregion


    //#region CARREGAMENTO DROPDOWNS.

    const loadCentroCustoDropDown = useCallback(async () => {

        try {
            setPageLoading(true);
            const result = await getCentroCustoDropDownUseCase.getCentroCustoDropDown();
            setDdlCentroCusto(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar centro de custo!'));
        } finally {
            setPageLoading(false);
        }

    }, [
        getCentroCustoDropDownUseCase,
        pushErrorNotification,
    ]);



    const loadOrdemCompraDropDown = useCallback(async () => {
        try {
            setPageLoading(true);
            const result = await getOrdemComprasDropDownUseCase.getOrdemComprasDropDown();
            setDdlOrdemCompra(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar ordens de compra!'));
        } finally {
            setPageLoading(false);
        }
    }, [getOrdemComprasDropDownUseCase]);


    const loadFornecedorDropDown = useCallback(async () => {
        try {
            setPageLoading(true);

            const result = await getFornecedorDropDownUseCase.getFornecedorDropDown();
            setDdlFornecedor(
                result.map((x) => ({
                    id: x.idFornecedor,
                    title: x.nomeFantasia
                }))
            );

        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar fornecedores!'));
        } finally {
            setPageLoading(false);
        }
    }, [getFornecedorDropDownUseCase,
        pushErrorNotification
    ]);

    const loadFuncionarioDropDown = useCallback(async () => {
        try {
            setPageLoading(true);

            const result = await getFuncionariosDropDownUseCase.getFuncionariosDropDown();
            setDdlFuncionario(result.map((x) => ({
                id: x.idFuncionario,
                title: x.nome
            })));

        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar funcionários!'));
        } finally {
            setPageLoading(false);
        }
    }, [getFuncionariosDropDownUseCase,
        pushErrorNotification
    ]);


    const loadPgtoMedicaoDropDown = useCallback(async () => {
        try {
            setPageLoading(true);

            const result = await getMedicaoTeceirizacaoDropDownUseCase.getMedicaoTeceirizacaoDropDown();
            setDdlPgtoMedicao(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar pagamentos de medição!'));
        } finally {
            setPageLoading(false);
        }
    }, [getCentroCustoDropDownUseCase]);

    //#endregion




    //#region CARREGAMENTO.


    const loadRateioGeralContaPagarLista = useCallback(async (funcState?: LoadRateioGeralListaState) => {

        try {

            const filteredIdsCentroCusto = funcState?.idsCentroCusto ?? filters.idsCentroCusto ?? [];
            const filteredNrNotaFiscal = evaluateParamValue(funcState?.nroNotaFiscal, filters.nroNotaFiscal);
            const filteredDataDe = evaluateParamValue(funcState?.dataDe, filters.dataDe);
            const filteredDataAte = evaluateParamValue(funcState?.dataAte, filters.dataAte);

            const filteredPage = funcState?.page ?? pagination.page;
            const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

            await getAllRateioGeralContaPagarUseCase.getAllRateioGeralContaPagarLista(
                filteredIdsCentroCusto,
                filteredNrNotaFiscal,
                filteredDataDe,
                filteredDataAte,
                filteredPage,
                filteredRowsPerPage,
            );

            setRateioGeralContaPagarLista(getAllRateioGeralContaPagarUseCase.items);

            pagination.setRowsPerPage(getAllRateioGeralContaPagarUseCase.pageSize);
            pagination.setTotalItems(getAllRateioGeralContaPagarUseCase.totalItems);
        } catch (e: unknown) {
            setRateioGeralContaPagarLista([]);
            throw new Error("Falha ao buscar Rateio Geral!");
        }
    }, [
        filters,
        pagination,
        getAllRateioGeralContaPagarUseCase,
    ]);




    //#endregion

    usePageStartBootstrap(async () => {
        try {

            if (rateioGeralContaPagarLista === undefined) {
                await loadRateioGeralContaPagarLista();
                await loadCentroCustoDropDown();
                await loadOrdemCompraDropDown();
                await loadFornecedorDropDown();
                await loadFuncionarioDropDown();
                await loadPgtoMedicaoDropDown();
            }
        } catch (e: unknown) {
            setErrorMessage(processError(e, "Falha ao buscar lista de rateio geral!")[0]);
        }

    });




    //#region NOVO/EDITAR.

    const handleNewRateioGeral = useCallback(() => {
        navigate("/RateioGeral_lista");
    }, [navigate]);

    const handleEditRateioGeral = useCallback((idContasPagar: number | null) => {
        navigate("/RateioGeral_cadastro", { state: { idContasPagar } });

    }, [navigate]);

    //#endregion




    //#region PAGINAÇÃO.


    const handleReload = useCallback(async () => {
        try {
            setLoading(true);
            await loadRateioGeralContaPagarLista();
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de rateio geral!'));
        } finally {
            setLoading(false);
        }
    }, [loadRateioGeralContaPagarLista]);


    const handlePageChange = useCallback(async (page: number) => {
        try {
            setLoading(true);
            pagination.setPage(page + 1);
            await loadRateioGeralContaPagarLista({ page: page + 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de Rateio Geral!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, loadRateioGeralContaPagarLista]);


    const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
        try {
            setLoading(true);
            pagination.setPage(1);
            pagination.setRowsPerPage(rowsPerPage);
            await loadRateioGeralContaPagarLista({ rowsPerPage, page: 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar Rateio Geral!'));
        } finally {
            setLoading(false);
        }
    }, [pagination,
        loadRateioGeralContaPagarLista,
        pushErrorNotification]);

    //#endregion






    //#region DELECAO

    const handleRateioGeralDeleteOpenState = useCallback((idContapagar: number | null, openState: boolean) => {
        if (openState) {
            if (idContapagar) {
                setSelectedIdRateioGeral(idContapagar);
                setOpenDeleteModal(true);
            } else {
                setSelectedIdRateioGeral(undefined);
                setOpenDeleteModal(false);
            }
        }
        setOpenDeleteModal(openState);
    }, []);


    const handleConfirmarDeletarRateioGeral = useCallback(async () => {
        try {

            setSaving(true);
            if (selectedIdRateioGeral) {
                await deleteRateioGeralUseCase.deleteRateioGeral(selectedIdRateioGeral)
                pushInfoNotification("Sucesso", "Rateio Geral Deletado com sucesso!", { isAutoFinish: true });
            } else {
                pushErrorNotification("Erro", "Não foi possivel deletar Rateio Geral!", { isAutoFinish: true });

            }

            await loadRateioGeralContaPagarLista()
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao Deletar rateio geral!"));
        } finally {
            setSaving(false);
            setOpenDeleteModal(false);
            setSelectedIdRateioGeral(undefined)
        }
    }, [
        selectedIdRateioGeral,
        deleteRateioGeralUseCase,
        loadRateioGeralContaPagarLista,
        pushErrorNotification,
        pushInfoNotification]);


    //#endregion



    //#region RELATÓRIO.

    const handleRelatorioOpenState = useCallback((openState: boolean) => {
        setOpenRelatorioModal(openState);

    }, []);

    const handleRateioReportSubmit = useCallback(async (form: ReportRateioGeralForm) => {
        try {
            setSaving(true);

            await startReportViewerProcess({
                fetchCallback: async (requiredMimeType) => {
                    return await getReportRateioGeralListaUseCase.execute({
                        ...form,
                        requiredMimeType
                    });
                }
            });

        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao gerar relatório de rateio geral!"));
        } finally {
            setSaving(false);
        }
    }, [
        getReportRateioGeralListaUseCase,
        startReportViewerProcess,
        pushErrorNotification
    ]);


    //#endregion



    //#region PESQUISA.

    const handleIdCentroCustoChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setIdsCentroCusto(newValues)
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadRateioGeralContaPagarLista({ idsCentroCusto: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar Rateio Geral!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters,
            pagination,
            loadRateioGeralContaPagarLista,]);


    const handleNroNotaFiscalChange = useDebouncedCallback(
        debouncedRef,
        (newValue: string | null) => {
            filters.setNroNotaFiscal(newValue);
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadRateioGeralContaPagarLista({ nroNotaFiscal: newValue, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar Rateio Geral!"));
                } finally {
                    setLoading(false);
                }
            };
        }, [filters,
        pagination,
        loadRateioGeralContaPagarLista]
    );


    const handleDataPagoRangeChange = useDebouncedCallback(
        debouncedRef,
        (dataDe: Date | null, dataAte: Date | null) => {
            filters.setDataDe(dataDe);
            filters.setDataAte(dataAte);

            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadRateioGeralContaPagarLista({ dataDe, dataAte, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar Rateio Geral!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadRateioGeralContaPagarLista]
    );


    const handleClearFilters = useCallback(async () => {
        try {
            setLoading(true);
            filters.clearFilters();
            pagination.setPage(1);
            await loadRateioGeralContaPagarLista({
                ...initialBancoPagamentoFiltersValues,
                page: 1
            });
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, 'Falha ao buscar indices de reajuste!'));
        } finally {
            setLoading(false);
        }
    }, [filters,
        pagination,
        loadRateioGeralContaPagarLista,
        pushErrorNotification]);

    //#endregion 

    return {
        loading,
        saving,
        rateioGeralContaPagarLista: rateioGeralContaPagarLista ?? [],
        errorMessage,
        filters,
        pagination,
        openDeleteModal,
        selectedIdRateioGeral,
        reportViewerState,
        ddlCentroCusto: ddlCentroCusto ?? [],
        ddlOrdemCompra: ddlOrdemCompra ?? [],
        ddlFornecedor: ddlFornecedor ?? [],
        ddlFuncionario: ddlFuncionario ?? [],
        ddlPgtoMedicao: ddlPgtoMedicao ?? [],
        openRelatorioModal,

        handleRelatorioOpenState,
        handleRateioReportSubmit,
        handleNewRateioGeral,
        handleEditRateioGeral,
        handleIdCentroCustoChange,
        handleNroNotaFiscalChange,
        handleDataPagoRangeChange,
        handleReload,
        handlePageChange,
        handleRowsPerPageChange,
        handleClearFilters,
        handleRateioGeralDeleteOpenState,
        handleConfirmarDeletarRateioGeral

    };
}


